import type { VNode } from 'vue';
import { h, Text } from 'vue';

const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:', 'ftp:'] as const;

const isValidUrlScheme = (url: string): boolean => ALLOWED_SCHEMES.some((scheme) => url
  .trim()
  .toLowerCase()
  .startsWith(scheme));

interface MarkdownMatch {
  start: number;
  end: number;
  tag: string;
  content: string;
  url?: string;
}

const findAllMatches = (text: string): MarkdownMatch[] => {
  const patterns: { regex: RegExp; tag: string; isLink?: boolean }[] = [
    { regex: /\*\*([\s\S]+?)\*\*/g, tag: 'strong' },
    { regex: /~~([\s\S]+?)~~/g, tag: 's' },
    { regex: /\[([^\]]+)\]\(([^)]+)\)/g, tag: 'a', isLink: true },
  ];

  const matches: MarkdownMatch[] = [];

  for (const { regex, tag, isLink } of patterns) {
    for (const match of text.matchAll(regex)) {
      matches.push({
        start: match.index ?? 0,
        end: (match.index ?? 0) + match[0].length,
        tag,
        content: match[1],
        url: isLink ? match[2]?.trim() : undefined,
      });
    }
  }

  return matches;
};

const filterNonOverlapping = (matches: MarkdownMatch[]): MarkdownMatch[] => {
  const sorted = [...matches].sort((a, b) => a.start - b.start || b.end - a.end);
  const result: MarkdownMatch[] = [];
  let lastEnd = 0;

  for (const match of sorted) {
    if (match.start >= lastEnd) {
      result.push(match);
      lastEnd = match.end;
    }
  }

  return result;
};

const parseMarkdownString = (text: string): (string | VNode)[] => {
  if (!text) {
    return [text];
  }

  const allMatches = findAllMatches(text);
  if (allMatches.length === 0) {
    return [text];
  }

  const matches = filterNonOverlapping(allMatches);
  const result: (string | VNode)[] = [];
  let pos = 0;

  for (const match of matches) {
    if (match.start > pos) {
      result.push(text.substring(pos, match.start));
    }

    const parsedContent = parseMarkdownString(match.content);

    if (match.tag === 'a') {
      if (match.url && isValidUrlScheme(match.url)) {
        result.push(h('a', { href: match.url, target: '_blank', rel: 'noopener noreferrer' }, parsedContent));
      } else {
        result.push(...parsedContent);
      }
    } else {
      result.push(h(match.tag, parsedContent));
    }

    pos = match.end;
  }

  if (pos < text.length) {
    result.push(text.substring(pos));
  }

  return result.length > 0 ? result : [text];
};

const isVNodeLike = (value: unknown): value is VNode => value !== null
  && typeof value === 'object'
  && 'type' in value
  && (value as any).type !== undefined;

const parseMarkdownInVNode = (node: VNode): VNode | (string | VNode)[] => {
  if (node.type === Text) {
    const text = typeof node.children === 'string' ? node.children : '';
    return parseMarkdownString(text);
  }

  if (!node.children) {
    return node;
  }

  let processedChildren: (string | VNode)[];

  if (Array.isArray(node.children)) {
    processedChildren = node.children.flatMap((child) => {
      if (typeof child === 'string') {
        return parseMarkdownString(child);
      }
      if (isVNodeLike(child)) {
        const result = parseMarkdownInVNode(child);
        return Array.isArray(result) ? result : [result];
      }
      return [];
    });
  } else if (typeof node.children === 'string') {
    processedChildren = parseMarkdownString(node.children);
  } else if (isVNodeLike(node.children)) {
    const result = parseMarkdownInVNode(node.children);
    processedChildren = Array.isArray(result) ? result : [result];
  } else {
    return node;
  }

  return h(node.type as any, { ...node.props }, processedChildren);
};

export const parseAllMarkdown = (content: string | VNode | (string | VNode)[]): (string | VNode)[] => {
  if (typeof content === 'string') {
    return parseMarkdownString(content);
  }

  if (Array.isArray(content)) {
    return content.flatMap((item) => {
      if (typeof item === 'string') {
        return parseMarkdownString(item);
      }
      if (isVNodeLike(item)) {
        const result = parseMarkdownInVNode(item);
        return Array.isArray(result) ? result : [result];
      }
      return [item];
    });
  }

  if (isVNodeLike(content)) {
    const result = parseMarkdownInVNode(content);
    return Array.isArray(result) ? result : [result];
  }

  return [content];
};
